import { useCallback, useEffect, useState } from "react";
import { apiGet } from "./client";

export type QueryState<TResult> = {
  data: TResult | undefined;
  error: string | undefined;
  isLoading: boolean;
  // Every write path calls this rather than revalidating a route: there is no
  // server render to invalidate any more, so a listing refreshes by asking
  // again.
  refresh: () => void;
};

export const useApiQuery = <TResult>(path: string): QueryState<TResult> => {
  const [data, setData] = useState<TResult>();
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    let isCurrent = true;

    setIsLoading(true);

    apiGet<TResult>(path)
      .then((result) => {
        if (isCurrent) {
          setData(result);
          setError(undefined);
        }
      })
      .catch((failure: Error) => {
        if (isCurrent) {
          setError(failure.message);
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    // A path change or an unmount mid-flight must not write the old answer
    // into state -- switching listings quickly would otherwise show the
    // previous one's rows.
    return () => {
      isCurrent = false;
    };
  }, [path, reloadCount]);

  const refresh = useCallback(() => setReloadCount((count) => count + 1), []);

  return { data, error, isLoading, refresh };
};
